/*
  meltX global.js
  Progressive enhancement only. Every core action works without JS.
  Deferred, runs once after DOM parse. No modules, no dependencies.
  No em dash characters anywhere.
*/
(function () {
  "use strict";

  var CFG = window.meltx || {};
  var S = CFG.strings || {};
  var R = CFG.routes || {};

  /* ------------------------------------------------------------------ utils */
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function announce(message) {
    var el = qs("#a11y-status");
    if (!el) return;
    el.textContent = "";
    window.requestAnimationFrame(function () { el.textContent = message; });
  }

  function withDelimiters(number, precision, thousands, decimalMark) {
    number = (number || 0).toFixed(precision);
    var parts = number.split(".");
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
    var frac = parts[1] ? decimalMark + parts[1] : "";
    return whole + frac;
  }

  function formatMoney(cents) {
    var format = CFG.moneyFormat || "{{amount}}";
    var value = cents / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, name) {
      switch (name) {
        case "amount": return withDelimiters(value, 2, ",", ".");
        case "amount_no_decimals": return withDelimiters(value, 0, ",", ".");
        case "amount_with_comma_separator": return withDelimiters(value, 2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withDelimiters(value, 0, ".", ",");
        case "amount_with_space_separator": return withDelimiters(value, 2, " ", ".");
        case "amount_no_decimals_with_space_separator": return withDelimiters(value, 0, " ", ".");
        default: return withDelimiters(value, 2, ",", ".");
      }
    });
  }
  CFG.formatMoney = formatMoney;

  var FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function focusable(root) {
    return qsa(FOCUSABLE, root).filter(function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement; });
  }

  /* --------------------------------------------------------------- drawers */
  var openStack = [];
  function lockScroll(lock) { document.body.classList.toggle("overlay-open", lock); }

  var Drawer = {
    open: function (id, opener) {
      var drawer = document.getElementById(id);
      if (!drawer || drawer.classList.contains("is-open")) return;
      drawer.classList.add("is-open");
      drawer.__opener = opener || document.activeElement;
      lockScroll(true);
      openStack.push(drawer);
      var target = drawer.querySelector("[data-drawer-initial]") || focusable(drawer)[0] || drawer;
      window.requestAnimationFrame(function () { target.focus(); });
      document.addEventListener("keydown", Drawer._key);
      drawer.dispatchEvent(new CustomEvent("drawer:open"));
    },
    close: function (drawer) {
      if (typeof drawer === "string") drawer = document.getElementById(drawer);
      if (!drawer || !drawer.classList.contains("is-open")) return;
      drawer.classList.remove("is-open");
      openStack = openStack.filter(function (d) { return d !== drawer; });
      if (!openStack.length) { lockScroll(false); document.removeEventListener("keydown", Drawer._key); }
      var opener = drawer.__opener;
      if (opener && typeof opener.focus === "function") opener.focus();
    },
    _key: function (e) {
      var current = openStack[openStack.length - 1];
      if (!current) return;
      if (e.key === "Escape") { e.preventDefault(); Drawer.close(current); return; }
      if (e.key === "Tab") {
        var f = focusable(current);
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  };
  CFG.Drawer = Drawer;

  document.addEventListener("click", function (e) {
    var opener = e.target.closest("[data-drawer-open]");
    if (opener) { e.preventDefault(); Drawer.open(opener.getAttribute("data-drawer-open"), opener); return; }
    var closer = e.target.closest("[data-drawer-close]");
    if (closer) { e.preventDefault(); Drawer.close(closer.closest(".drawer")); return; }
    if (e.target.classList && e.target.classList.contains("drawer__overlay")) { Drawer.close(e.target.closest(".drawer")); }
  });

  /* ------------------------------------------------------------------- cart */
  function updateCartCount(count) {
    qsa("[data-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.hidden = !count;
    });
    var label = qs("[data-cart-link]");
    if (label && S.cartCount) label.setAttribute("aria-label", S.cartCount.replace("{{ count }}", count));
  }

  function swapSection(html, selector) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, "text/html");
    var fresh = parsed.querySelector(selector);
    var current = document.querySelector(selector);
    if (fresh && current) current.replaceWith(fresh);
  }

  function updateCartUI(data) {
    if (typeof data.item_count === "number") updateCartCount(data.item_count);
    if (data.sections) {
      swapSection(data.sections["cart-drawer"], "[data-cart-content]");
      swapSection(data.sections["main-cart"], "[data-main-cart-content]");
    }
  }

  async function mutateCart(body) {
    var sections = ["cart-drawer"];
    if (document.getElementById("shopify-section-main-cart")) sections.push("main-cart");
    body.sections = sections.join(",");
    body.sections_url = window.location.pathname;
    var res = await fetch(R.cart_change_url + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/javascript" },
      body: JSON.stringify(body)
    });
    var data = await res.json();
    updateCartUI(data);
    announce(S.loading || "");
    document.dispatchEvent(new CustomEvent("cart:changed", { detail: data }));
  }

  document.addEventListener("change", function (e) {
    var line = e.target.closest("[data-cart-key]");
    if (line && e.target.matches("input.qty__input")) {
      var qty = parseInt(e.target.value, 10);
      if (isNaN(qty) || qty < 0) return;
      mutateCart({ id: line.getAttribute("data-cart-key"), quantity: qty });
    }
  });
  document.addEventListener("click", function (e) {
    var remove = e.target.closest("[data-cart-remove]");
    if (remove) { e.preventDefault(); mutateCart({ id: remove.getAttribute("data-cart-remove"), quantity: 0 }); }
  });

  /* ------------------------------------------------------- custom elements */
  if ("customElements" in window) {

    customElements.define("product-form", class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector("form");
        if (!this.form) return;
        this.button = this.querySelector('[type="submit"]');
        this.errorBox = this.querySelector("[data-form-error]");
        this.form.addEventListener("submit", this.onSubmit.bind(this));
      }
      label(text) {
        var l = this.button && this.button.querySelector("[data-btn-label]");
        if (l && text) l.textContent = text;
      }
      async onSubmit(e) {
        if (!window.fetch || !window.DOMParser) return; /* native POST fallback */
        e.preventDefault();
        var original = this.button.querySelector("[data-btn-label]");
        var originalText = original ? original.textContent : "";
        this.button.setAttribute("aria-disabled", "true");
        this.button.classList.add("is-loading");
        this.label(S.adding);
        if (this.errorBox) this.errorBox.hidden = true;
        try {
          var fd = new FormData(this.form);
          fd.append("sections", "cart-drawer");
          fd.append("sections_url", window.location.pathname);
          var res = await fetch(R.cart_add_url + ".js", {
            method: "POST",
            headers: { "Accept": "application/javascript", "X-Requested-With": "XMLHttpRequest" },
            body: fd
          });
          var data = await res.json();
          if (res.status !== 200) { this.showError(data.description || data.message); return; }
          updateCartUI(data);
          announce(S.itemAdded);
          if (CFG.cartType === "drawer") { Drawer.open("CartDrawer", this.button); }
          else { window.location.href = R.cart_url; }
          document.dispatchEvent(new CustomEvent("cart:added", { detail: data }));
        } catch (err) {
          this.showError();
        } finally {
          this.button.removeAttribute("aria-disabled");
          this.button.classList.remove("is-loading");
          this.label(originalText);
        }
      }
      showError(message) {
        announce(message || "");
        if (this.errorBox) { this.errorBox.textContent = message || ""; this.errorBox.hidden = !message; }
      }
    });

    customElements.define("quantity-input", class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector("input");
        var self = this;
        qsa("button", this).forEach(function (btn) {
          btn.addEventListener("click", function (e) {
            e.preventDefault();
            var step = btn.getAttribute("data-qty-change") === "increase" ? 1 : -1;
            var min = parseInt(self.input.getAttribute("min") || "0", 10);
            var value = parseInt(self.input.value || "0", 10) + step;
            if (value < min) value = min;
            self.input.value = value;
            self.input.dispatchEvent(new Event("change", { bubbles: true }));
          });
        });
      }
    });

    customElements.define("variant-selects", class extends HTMLElement {
      connectedCallback() {
        var data = this.querySelector('script[type="application/json"]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.form = document.getElementById(this.getAttribute("data-form"));
        this.priceTarget = document.getElementById(this.getAttribute("data-price"));
        this.addEventListener("change", this.onChange.bind(this));
      }
      selectedOptions() {
        var groups = {};
        qsa("select[data-option-index]", this).forEach(function (s) { groups[s.getAttribute("data-option-index")] = s.value; });
        qsa('input[type="radio"][data-option-index]:checked', this).forEach(function (r) { groups[r.getAttribute("data-option-index")] = r.value; });
        return Object.keys(groups).sort().map(function (k) { return groups[k]; });
      }
      onChange() {
        var idField = this.querySelector('input[name="id"]:checked, select[name="id"]');
        var variant;
        if (idField) {
          variant = this.variants.find(function (v) { return String(v.id) === String(idField.value); });
        } else {
          var chosen = this.selectedOptions();
          variant = this.variants.find(function (v) { return v.options.every(function (opt, i) { return opt === chosen[i]; }); });
        }
        this.update(variant);
      }
      update(variant) {
        var idInput = this.form && this.form.querySelector('input[type="hidden"][name="id"]');
        var button = this.form && this.form.querySelector("[data-add-button]");
        var label = button && button.querySelector("[data-btn-label]");
        if (!variant) {
          if (button) button.setAttribute("disabled", "disabled");
          if (label) label.textContent = S.unavailable;
          return;
        }
        if (idInput) idInput.value = variant.id;
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
        if (button) {
          if (variant.available) { button.removeAttribute("disabled"); if (label) label.textContent = S.addToCart; }
          else { button.setAttribute("disabled", "disabled"); if (label) label.textContent = S.soldOut; }
        }
        if (this.priceTarget) this.priceTarget.innerHTML = this.priceHTML(variant);
        document.dispatchEvent(new CustomEvent("variant:change", { detail: { variant: variant } }));
      }
      priceHTML(variant) {
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        var html = '<span class="price' + (onSale ? " price--on-sale" : "") + '"><span class="price__current">' + formatMoney(variant.price) + "</span>";
        if (onSale) html += '<span class="price__compare">' + formatMoney(variant.compare_at_price) + "</span>";
        return html + "</span>";
      }
    });
  }

  /* ------------------------------------------------------------ auto submit */
  document.addEventListener("change", function (e) {
    if (e.target.matches("[data-auto-submit]") && e.target.form) { e.target.form.submit(); }
  });

  /* -------------------------------------------------------------- accordions */
  document.addEventListener("click", function (e) {
    var trigger = e.target.closest(".accordion__trigger");
    if (!trigger) return;
    var expanded = trigger.getAttribute("aria-expanded") === "true";
    trigger.setAttribute("aria-expanded", String(!expanded));
    var panel = document.getElementById(trigger.getAttribute("aria-controls"));
    if (panel) panel.hidden = expanded;
  });

  /* --------------------------------------------------------------- gallery */
  function activateMedia(root, id) {
    if (!root) return;
    qsa(".product-media__slide", root).forEach(function (s) { s.classList.toggle("is-active", s.getAttribute("data-media-id") === String(id)); });
    qsa(".product-media__thumb", root).forEach(function (t) { t.classList.toggle("is-active", t.getAttribute("data-media-target") === String(id)); });
  }
  document.addEventListener("click", function (e) {
    var thumb = e.target.closest("[data-media-target]");
    if (!thumb) return;
    activateMedia(thumb.closest("[data-product-gallery]"), thumb.getAttribute("data-media-target"));
  });
  document.addEventListener("variant:change", function (e) {
    if (e.detail && e.detail.variant && e.detail.variant.featured_media) {
      activateMedia(qs("[data-product-gallery]"), e.detail.variant.featured_media.id);
    }
  });

  /* ----------------------------------------------------------- scroll reveal */
  function initReveal() {
    var items = qsa(".reveal");
    if (!items.length) return;
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!("IntersectionObserver" in window) || reduce) {
      items.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add("is-visible"); io.unobserve(entry.target); }
      });
    }, { rootMargin: "0px 0px -8% 0px" });
    items.forEach(function (el) { io.observe(el); });

    /* Safety net: a very fast scroll or an anchor jump can skip past a section
       without the observer ever seeing it intersect, which would leave that
       section stuck at opacity 0. Reveal anything at or above the fold. */
    var sweeping = false;
    function sweep() {
      sweeping = false;
      qsa(".reveal:not(.is-visible)").forEach(function (el) {
        if (el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add("is-visible");
          io.unobserve(el);
        }
      });
    }
    function queueSweep() {
      if (sweeping) return;
      sweeping = true;
      window.requestAnimationFrame(sweep);
    }
    window.addEventListener("scroll", queueSweep, { passive: true });
    window.addEventListener("resize", queueSweep, { passive: true });
    window.addEventListener("load", queueSweep);
  }
  initReveal();
  document.addEventListener("shopify:section:load", initReveal);

  /* ------------------------------------------------- sticky reveal bars */
  (function () {
    if (!("IntersectionObserver" in window)) return;
    function wire(bar, trigger, bodyClass) {
      if (!bar || !trigger) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var show = !e.isIntersecting;
          bar.classList.toggle("is-visible", show);
          if (bodyClass) document.body.classList.toggle(bodyClass, show);
        });
      }, { rootMargin: "0px 0px -20% 0px" });
      io.observe(trigger);
    }
    /* Product page: variant aware sticky add to cart. */
    wire(qs("[data-sticky-atc]"), qs("[data-add-button]"), "atc-visible");
    /* Everywhere else: global order bar, revealed once the hero CTA (or a top sentinel) scrolls away. */
    wire(qs("[data-order-bar]"), qs("[data-order-bar-trigger]") || qs("[data-order-bar-sentinel]"), "order-bar-active");
  })();

})();
